import { z } from "zod"

import {
  GlAccountBalance,
  GlAccountClassification,
  GlAccountCurrency,
  GlAccountSubType,
  GlAccountType,
  ReportingAccountType,
} from "@/graphql/codegen/graphql"

const ACCOUNT_NUMBER_MIN = 1 // AS400 constraint
const ACCOUNT_NUMBER_MAX = 9999 // AS400 constraint
const SUB_ACCOUNT_NUMBER_MIN = 0 // AS400 constraint
const SUB_ACCOUNT_NUMBER_MAX = 99 // AS400 constraint

const ACCOUNT_NUMBER_MESSAGE = `Account Number must be between ${ACCOUNT_NUMBER_MIN} and ${ACCOUNT_NUMBER_MAX}`
const SUB_ACCOUNT_NUMBER_MESSAGE = `Sub Account Number must be between ${SUB_ACCOUNT_NUMBER_MIN} and ${SUB_ACCOUNT_NUMBER_MAX}`

export const glAccountFormSchema = z
  .object({
    reportingAccountType: z.nativeEnum(ReportingAccountType),
    parentGlAccountId: z.string().nullish(),
    company: z.string(),
    costCenter: z.number().int().positive().nullish(),
    number: z
      .number({ invalid_type_error: ACCOUNT_NUMBER_MESSAGE, required_error: ACCOUNT_NUMBER_MESSAGE })
      .int(ACCOUNT_NUMBER_MESSAGE)
      .min(ACCOUNT_NUMBER_MIN, ACCOUNT_NUMBER_MESSAGE)
      .max(ACCOUNT_NUMBER_MAX, ACCOUNT_NUMBER_MESSAGE),
    subNumber: z
      .string()
      .regex(/^\d{0,2}$/, SUB_ACCOUNT_NUMBER_MESSAGE)
      .nullable()
      .optional(),
    type: z.nativeEnum(GlAccountType).nullish(),
    subType: z.nativeEnum(GlAccountSubType).nullish(),
    classification: z.nativeEnum(GlAccountClassification).nullish(),
    currency: z.nativeEnum(GlAccountCurrency),
    balance: z.nativeEnum(GlAccountBalance).optional(),
    title: z.string().min(1, "Account Title is required").max(35, "Account Title must be 35 characters or less"),
    description: z.string().max(50, "Description must be 50 characters or less").optional(),
  })
  .superRefine((values, ctx) => {
    if (values.reportingAccountType === ReportingAccountType.Posting && !values.parentGlAccountId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Parent Account is required for posting accounts",
        path: ["parentGlAccountId"],
      })
    }

    if (
      values.reportingAccountType === ReportingAccountType.Posting &&
      (values.subNumber === null || values.subNumber === undefined || values.subNumber === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: SUB_ACCOUNT_NUMBER_MESSAGE,
        path: ["subNumber"],
      })
    }
  })

export type GlAccountFormValues = z.infer<typeof glAccountFormSchema>
